import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import usePlacesTop from '../../hooks/usePlacesTop';
import { t } from '../../i18n';

// ============================================
// „Darts in deiner Stadt“ (2026-09-20) – Top-Orte als Chips.
// Startseite (volle Variante, 20 Städte) und Footer (compact, 10 Städte,
// nur Links). Daten kommen einmal über usePlacesTop (Modul-Cache), d. h.
// keine zweite API-Last für den Footer. Leer/Fehler → rendert nichts.
// ============================================

const ACCENT = '#3fced1';

export interface TopCitiesSectionProps {
  /** Anzahl Städte (Startseite 20, Footer 10) */
  limit?: number;
  /** Kompakte Variante für den Footer: nur Überschrift + Links */
  variant?: 'default' | 'compact';
  /** Zusätzliche Klassen am Wurzelelement (z. B. Abstand im Footer) */
  className?: string;
}

const TopCitiesSection: React.FC<TopCitiesSectionProps> = ({ limit = 20, variant = 'default', className }) => {
  const { places } = usePlacesTop(limit);
  if (places.length === 0) return null;

  if (variant === 'compact') {
    return (
      <div className={['min-w-0', className].filter(Boolean).join(' ')} data-testid="top-cities-compact">
        <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest text-gray-500">
          {t('web.component.top-cities-section.darts-in-deiner-stadt')}
        </h3>
        <ul className="flex flex-wrap gap-x-4 gap-y-2">
          {places.map((p) => (
            <li key={p.slug}>
              <Link
                to={p.path}
                className="rounded-md text-sm text-gray-400 transition-colors hover:text-[#3fced1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
              >
                {p.name}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/darten-in"
              className="inline-flex items-center gap-1 rounded-md text-sm font-semibold text-gray-300 transition-colors hover:text-[#3fced1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              {t('web.component.top-cities-section.alle-orte')} <ArrowRight size={13} />
            </Link>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <section aria-label={t('web.component.top-cities-section.darts-in-deiner-stadt')} data-testid="top-cities-section" className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">
            {t('web.component.top-cities-section.darts-in-deiner-stadt')}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {t('web.component.top-cities-section.vereine-spielstaetten-shops-und')}
          </p>
        </div>
        <Link
          to="/darten-in"
          className="flex shrink-0 items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1]"
        >
          {t('web.component.top-cities-section.alle-orte')} <ArrowRight size={13} />
        </Link>
      </div>
      <ul className="flex flex-wrap gap-2">
        {places.map((p) => (
          <li key={p.slug}>
            <Link
              to={p.path}
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:border-[#3fced1]/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              <MapPin size={13} style={{ color: ACCENT }} className="shrink-0" />
              <span>{p.name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default TopCitiesSection;
